/**
 * `Util` — global helper namespace exposed to Aktion programs.
 *
 * These are pure data transformations available everywhere as
 * `Util.<method>(...)`. They replace the former `@-builtin` catalog
 * (`@Count`, `@Filter`, `@Format`, …) which was removed.
 *
 * The namespace is intentionally open: add methods here and they
 * become available to authors without any other wiring. Pair with
 * documentation in `src/prompt/generator.ts` so the LLM-facing prompt
 * teaches the new entry.
 */

const toNumber = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
  return 0;
};

const toArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/*
 * Parameter types of the `Util` helpers. They are printed into the
 * `aktion-runtime/dsl` declarations (scripts/dsl-types/runtime-types.ts), so
 * each is exported and self-contained: no DOM type, no runtime-local name.
 * They describe what the helpers READ; every helper still guards its input at
 * runtime, because an untyped `.aktion` program reaches them too.
 */

/**
 * An array the list helpers read. A nullish value (data not loaded yet) is
 * treated as empty; so is any other non-array (a `Set`, a `Map`, an object).
 */
export type UtilList<T> = readonly T[] | null | undefined;

/**
 * What the helpers whose result does not depend on the element type (`count`,
 * `sum`, `avg`, `min`, `max`, `join`) read: a {@link UtilList}, or a value
 * typed `unknown` — an untyped `$query`'s `data` — which they guard at runtime,
 * reading any non-array as empty. A value of a known non-array type (a string,
 * a `Set`, an object) is still rejected: it would be read as `[]`.
 */
export type UtilAggregateInput<A> = unknown extends A ? A : UtilList<unknown>;

/**
 * What `pick` returns: the picked keys of `T`. When `T` may be `null` /
 * `undefined` (data not loaded yet) every key is optional, because a nullish
 * input gives `{}`.
 */
export type UtilPicked<T, K extends PropertyKey> =
  [T] extends [object] ? Pick<T, K & keyof T> : Partial<Pick<NonNullable<T>, K & keyof NonNullable<T>>>;

/** What `omit` returns: `T` without the omitted keys — every key optional when `T` may be nullish, as for {@link UtilPicked}. */
export type UtilOmitted<T, K extends PropertyKey> =
  [T] extends [object] ? Omit<T, K> : Partial<Omit<NonNullable<T>, K>>;

/** A field of the row type, or a dotted path into it (`"owner.name"`). */
export type UtilFieldPath<T> = (T extends object ? keyof T & string : never) | (string & {});

/** The comparison operators of `filter` / `find` / `partition`. Any other string matches nothing. */
export type UtilCompareOp = "==" | "!=" | ">" | "<" | ">=" | "<=" | "contains" | "startsWith" | "endsWith";

/** The options object of `format` (each key is ignored unless it has the right type). */
export interface UtilNumberFormatOptions {
  /** ISO 4217 code for `"currency"` mode (default `"USD"`). */
  readonly currency?: string;
  /** BCP 47 locale (default: the host's). */
  readonly locale?: string;
  /** Fixed number of fraction digits. */
  readonly decimals?: number;
}

/** A `File` / `Blob`, structurally (what `FileUpload` hands over). */
export interface UtilBlobLike {
  readonly size: number;
  readonly type: string;
  text(): Promise<string>;
  arrayBuffer(): Promise<ArrayBuffer>;
}

/** The options object of `readFile`. */
export interface UtilReadFileOptions {
  /** `"text"` (default) the UTF-8 text, `"dataUrl"` a `data:` URI, `"base64"` that URI's payload. */
  readonly as?: "text" | "dataUrl" | "base64";
  /** Resolve `""` without reading when the file is larger than this many bytes. */
  readonly maxSize?: number;
}

/** A window feature switch: on is `true`, `1`, `"1"`, `"yes"` or `"true"`; anything else is off. */
export type UtilWindowFlag = boolean | 0 | 1 | "0" | "1" | "yes" | "no" | "true" | "false";

/** The `window.open` features `openUrl` / `openWindow` pass on. Any other key is dropped. */
export interface UtilWindowFeatures {
  readonly width?: number;
  readonly height?: number;
  readonly left?: number;
  readonly top?: number;
  readonly screenX?: number;
  readonly screenY?: number;
  readonly innerWidth?: number;
  readonly innerHeight?: number;
  readonly popup?: UtilWindowFlag;
  readonly menubar?: UtilWindowFlag;
  readonly toolbar?: UtilWindowFlag;
  readonly location?: UtilWindowFlag;
  readonly status?: UtilWindowFlag;
  readonly resizable?: UtilWindowFlag;
  readonly scrollbars?: UtilWindowFlag;
  readonly noopener?: UtilWindowFlag;
  readonly noreferrer?: UtilWindowFlag;
}

/** The options object of `openUrl`. */
export interface UtilOpenUrlOptions {
  /** `"_blank"` (default, a new tab) or a window NAME, which reuses one window across calls. */
  readonly target?: "_blank" | "_self" | "_parent" | "_top" | (string & {});
  /** A features object, or a `"key=value,…"` string (re-parsed against the same allow-list). */
  readonly features?: UtilWindowFeatures | string;
  /** Defaults to on for `_blank` and off for a named target. */
  readonly noopener?: UtilWindowFlag;
  readonly noreferrer?: UtilWindowFlag;
}

/** The options object of `openWindow`. */
export interface UtilOpenWindowOptions {
  /** A window name, which reuses one window across calls (default `"_blank"`). */
  readonly name?: string;
  /** The same features `openUrl` takes. */
  readonly features?: UtilWindowFeatures | string;
}

/** The config `webManifest` reads (each key is ignored unless it is a string, or an array for `icons`). */
export interface UtilWebManifestConfig {
  readonly name?: string;
  readonly shortName?: string;
  readonly startUrl?: string;
  readonly display?: "fullscreen" | "standalone" | "minimal-ui" | "browser" | (string & {});
  readonly backgroundColor?: string;
  readonly themeColor?: string;
  readonly description?: string;
  readonly icons?: readonly { readonly src?: string; readonly sizes?: string; readonly type?: string }[];
}

/** The sanitised Web App Manifest `webManifest` returns (snake_case, defaults filled in). */
export interface UtilWebManifest {
  name: string;
  short_name: string;
  start_url: string;
  display: string;
  background_color: string;
  theme_color: string;
  description?: string;
  icons?: { src: string; sizes: string; type: string }[];
}

/**
 * A `Blob`-ish value, structurally. Deliberately NOT `instanceof Blob`: a
 * program may be handed a `File` from a different realm (an `<iframe>`'s picker,
 * a jsdom test), where the constructor identity differs but the interface does
 * not.
 */
type BlobLike = {
  size?: number;
  type?: string;
  text?: () => Promise<string>;
  arrayBuffer?: () => Promise<ArrayBuffer>;
};

const isBlobLike = (v: unknown): v is BlobLike =>
  Boolean(v) && typeof v === "object" &&
  (typeof (v as BlobLike).text === "function" || typeof (v as BlobLike).arrayBuffer === "function");

/**
 * The one file out of whatever `FileUpload` handed over.
 *
 * `onSelect` is invoked with the whole pick — always a `File[]`: the accepted
 * files, or the remaining ones after a remove — so the overwhelmingly common
 * call is `$util.readFile(files)` rather than `$util.readFile(files[0])`.
 * Accepting both removes the single most likely mistake at the call site:
 * indexing the pick is fine, but forgetting to is silent, and
 * `readFile(files)` would otherwise resolve `""` as though the file were
 * unreadable. A `FileList` the caller passes itself (an `<input>`'s `files`) is
 * read the same way.
 */
const firstBlob = (input: unknown): BlobLike | null => {
  if (isBlobLike(input)) return input;
  if (Array.isArray(input)) {
    const found = input.find((entry) => isBlobLike(entry));
    return found ? (found as BlobLike) : null;
  }
  // `FileList` is array-LIKE, not an array: it has `length` + index access and
  // nothing else, so neither branch above reaches it.
  if (input && typeof input === "object" && typeof (input as { length?: unknown }).length === "number") {
    const list = input as unknown as ArrayLike<unknown>;
    for (let index = 0; index < list.length; index += 1) {
      if (isBlobLike(list[index])) return list[index] as BlobLike;
    }
  }
  return null;
};

/**
 * Decode a blob as UTF-8 text.
 *
 * `Blob.prototype.text()` is the modern path and is what every current browser
 * and jsdom provide. The `FileReader` fallback is for hosts that predate it;
 * both are reached through this one function so a caller never has to know which
 * it got.
 */
const blobToText = (blob: BlobLike): Promise<string> => {
  if (typeof blob.text === "function") return blob.text().then((value) => String(value ?? ""));
  if (typeof blob.arrayBuffer === "function" && typeof TextDecoder !== "undefined") {
    return blob.arrayBuffer().then((buffer) => new TextDecoder().decode(new Uint8Array(buffer)));
  }
  return new Promise<string>((resolve, reject) => {
    if (typeof FileReader === "undefined") { reject(new Error("no FileReader")); return; }
    const reader = new FileReader();
    reader.onload = () => { resolve(String(reader.result ?? "")); };
    reader.onerror = () => { reject(reader.error ?? new Error("read failed")); };
    reader.readAsText(blob as unknown as Blob);
  });
};

/**
 * Encode a blob as a `data:` URI.
 *
 * Built from `arrayBuffer()` + `btoa` rather than `FileReader.readAsDataURL`
 * because the buffer path is available wherever `blobToText` is, keeps this
 * helper synchronous in structure, and — unlike `FileReader` — cannot be left
 * hanging by a host that never fires either callback. The chunked `btoa` is not
 * an optimisation: `String.fromCharCode(...bytes)` on a multi-megabyte file
 * spreads a million arguments onto the stack and throws `RangeError`.
 */
const blobToDataUrl = (blob: BlobLike): Promise<string> => {
  if (typeof blob.arrayBuffer !== "function" || typeof btoa === "undefined") {
    return Promise.reject(new Error("no arrayBuffer"));
  }
  return blob.arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const CHUNK = 8192;
    for (let at = 0; at < bytes.length; at += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
    }
    return `data:${blob.type || "application/octet-stream"};base64,${btoa(binary)}`;
  });
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === "object" && !Array.isArray(v);

/**
 * Schemes `$util.openUrl` / `OpenedWindow.navigate` will hand to the browser.
 *
 * An allow-list rather than a deny-list, because the dangerous set is open-ended
 * and grows: `javascript:` and `vbscript:` execute in the opened context,
 * `data:` and `blob:` let a program mint a document of its own and then point a
 * user at it from a trusted origin, and `file:` reaches the host filesystem. A
 * program under the `"safe"` global-access policy has no `window`, so these two
 * builtins are the only route it has to a new browsing context — which is
 * exactly why the gate belongs here rather than at each call site.
 *
 * `mailto:` and `tel:` are included because they are handed to the OS rather
 * than rendered, and a "contact" action is an ordinary reason to reach for
 * `openUrl`.
 */
const OPENABLE_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * Resolve a caller's URL against the document, and return it only if its scheme
 * is openable. `""` for everything else — an empty string is the single "no"
 * every caller below already branches on.
 *
 * RELATIVE URLS ARE SUPPORTED and are resolved against `document.baseURI`, so a
 * program may open `"/help"` without knowing its own origin. That is also why
 * the parse happens here and not in the caller: `new URL(text)` alone throws on
 * a relative input, and a `try`/`catch` at each site would silently turn a valid
 * relative path into "no".
 */
const openableUrl = (raw: unknown): string => {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return "";
  try {
    const base =
      (typeof document !== "undefined" && document.baseURI) ||
      (typeof location !== "undefined" ? location.href : "");
    const parsed = base ? new URL(text, base) : new URL(text);
    return OPENABLE_PROTOCOLS.has(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
};

/**
 * One reading of "on" for every window feature, wherever it was written.
 *
 * `openUrl` takes `noopener` BOTH as a top-level option and as a key inside
 * `features`, and the two used to disagree: the option was tested with
 * `=== true`, so `{noopener: "yes"}` read as false and silently switched OFF the
 * `_blank` default, while the same `"yes"` inside `features` read as true. One
 * value, opposite meanings, depending only on where the author put it.
 *
 * The set below is what a feature string can carry (`window.open` itself accepts
 * `1`/`yes`), plus the booleans an options bag naturally uses. Anything else —
 * `"no"`, `0`, `""`, `undefined` — is off.
 */
const isFeatureOn = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "yes" || value === "true";

/** The `UtilWindowFeatures` keys whose value type is `V`. */
type WindowFeatureKey<V> = {
  [K in keyof UtilWindowFeatures]-?: NonNullable<UtilWindowFeatures[K]> extends V ? K : never;
}[keyof UtilWindowFeatures];

/**
 * Window features that may be set, and how each is serialised. The element
 * types tie every entry to a `UtilWindowFeatures` key of the matching kind; the
 * other direction (every interface key is on a list) is pinned by
 * `tests/dsl-types-builtins.test.ts`.
 */
const WINDOW_FEATURE_NUMBERS: ReadonlySet<string> = new Set<WindowFeatureKey<number>>(["width", "height", "left", "top", "screenX", "screenY", "innerWidth", "innerHeight"]);
const WINDOW_FEATURE_FLAGS: ReadonlySet<string> = new Set<WindowFeatureKey<UtilWindowFlag>>(["popup", "menubar", "toolbar", "location", "status", "resizable", "scrollbars", "noopener", "noreferrer"]);

/**
 * Build the third argument to `window.open` from an options bag, dropping
 * anything not on the allow-list above.
 *
 * A STRING IS ACCEPTED AND RE-PARSED rather than passed through. `window.open`'s
 * feature string is a comma-separated `key=value` list, and forwarding an
 * author's string verbatim would let an unknown key reach the browser — which is
 * how a feature the allow-list exists to withhold gets set anyway. Parsing and
 * re-emitting keeps one code path and one list.
 *
 * Booleans are emitted as `yes`/`no`: every engine accepts that form, while the
 * bare key (`"popup"` with no value) is only honoured by some.
 */
const windowFeatures = (input: unknown, extra?: Record<string, boolean>): string => {
  const raw: Record<string, unknown> = {};
  if (typeof input === "string") {
    for (const part of input.split(",")) {
      const [key, value] = part.split("=");
      const name = (key ?? "").trim();
      if (name) raw[name] = value === undefined ? true : value.trim();
    }
  } else if (isObject(input)) {
    Object.assign(raw, input);
  }
  if (extra) {
    for (const [name, on] of Object.entries(extra)) {
      // Explicit caller intent wins over the defaults the builtin adds.
      if (!(name in raw) && on) raw[name] = true;
    }
  }
  const parts: string[] = [];
  for (const [name, value] of Object.entries(raw)) {
    if (WINDOW_FEATURE_NUMBERS.has(name)) {
      const size = Math.round(toNumber(value));
      if (Number.isFinite(size)) parts.push(`${name}=${size}`);
      continue;
    }
    if (WINDOW_FEATURE_FLAGS.has(name)) {
      parts.push(`${name}=${isFeatureOn(value) ? "yes" : "no"}`);
    }
  }
  return parts.join(",");
};

/** The four names that address an EXISTING context rather than naming a new one. */
const RESERVED_WINDOW_TARGETS = new Set(["_blank", "_self", "_parent", "_top"]);

/**
 * The handle `$util.openWindow()` hands back.
 *
 * Deliberately NOT the raw `Window`: a program under the `"safe"` policy must
 * not be given an object it can walk into `opener`, `document` or `parent`
 * through. Four members is the whole surface, and each one re-checks the
 * underlying context rather than trusting a flag captured at open time.
 */
export type OpenedWindow = {
  /** `false` when the browser refused (a popup blocker) or there was no `window` at all. */
  ok: boolean;
  /** Point the context at a URL. Returns `false` for a rejected scheme or a context that is gone. */
  navigate(url: string): boolean;
  /** Close the context. Safe to call twice, and on a context that was never opened. */
  close(): void;
  /** Live: `true` once the context has been closed, by this program or by the user. */
  readonly closed: boolean;
};

const CLOSED_WINDOW: OpenedWindow = {
  ok: false,
  navigate: () => false,
  close: () => {},
  get closed() {
    return true;
  },
};

const compare = (op: UtilCompareOp, a: unknown, b: unknown): boolean => {
  switch (op) {
    case "==": return a === b;
    case "!=": return a !== b;
    case ">": return toNumber(a) > toNumber(b);
    case "<": return toNumber(a) < toNumber(b);
    case ">=": return toNumber(a) >= toNumber(b);
    case "<=": return toNumber(a) <= toNumber(b);
    case "contains": {
      const haystack = String(a ?? "").toLowerCase();
      const needle = String(b ?? "").toLowerCase();
      return haystack.includes(needle);
    }
    case "startsWith": return String(a ?? "").toLowerCase().startsWith(String(b ?? "").toLowerCase());
    case "endsWith": return String(a ?? "").toLowerCase().endsWith(String(b ?? "").toLowerCase());
    default: return false;
  }
};

const cloneDeepValue = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map((v) => cloneDeepValue(v)) as unknown as T;
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
      out[k] = cloneDeepValue((value as Record<string, unknown>)[k]);
    }
    return out as unknown as T;
  }
  return value;
};

const getField = (item: unknown, field: string): unknown => {
  if (field === "") return item;
  if (item && typeof item === "object") {
    if (field.includes(".")) {
      let cursor: unknown = item;
      for (const part of field.split(".")) {
        if (cursor && typeof cursor === "object") {
          cursor = (cursor as Record<string, unknown>)[part];
        } else return undefined;
      }
      return cursor;
    }
    return (item as Record<string, unknown>)[field];
  }
  return undefined;
};

const toDate = (v: unknown): Date => {
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string" && v.trim() !== "") {
    if (!Number.isNaN(Number(v)) && /^-?\d+$/.test(v.trim())) return new Date(Number(v));
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
};

/* --- Durations ----------------------------------------------------------- *
 *
 * A *duration* is a length of time — a cooldown, a TTL, a poll interval, a
 * retention window — as opposed to a *date*, which is a point on a calendar.
 * Every API that takes one writes it in one of two grammars, and most accept
 * both:
 *
 *   simple    `250ms`, `30s`, `5m`, `2h`, `7d`   (Spring's `DurationStyle.SIMPLE`)
 *   ISO-8601  `PT30S`, `PT5M`, `P1D`, `P1DT12H`  (`java.time.Duration.parse`)
 *
 * So a form field that accepts one has to accept the other, and neither the
 * platform nor `Intl` parses either: `Date.parse` is for instants, and
 * `Intl.DurationFormat` only *formats*, is Baseline-2025, and takes a
 * components object rather than a string.
 *
 * THE PARSER IS DELIBERATELY STRICTER THAN ISO-8601 IN TWO PLACES, because the
 * servers on the other end are:
 *
 *   * YEARS AND MONTHS ARE REJECTED. `P1M` is a month, whose length depends on
 *     which month it is, so it cannot be expressed in seconds without a
 *     calendar. `java.time.Duration.parse` refuses them for the same reason,
 *     and a client that quietly resolved `P1M` to 30 days would send a number
 *     the server then disagreed with.
 *   * WEEKS ARE REJECTED. `P1W` is unambiguous (7 × 24h) but is a *period*
 *     designator: `Duration.parse` rejects it, so accepting it here would let a
 *     form submit a value its own backend cannot read.
 *
 * A UNITLESS NUMBER IS ALSO REJECTED. Spring's simple format reads a bare `500`
 * as milliseconds; a great many hand-rolled APIs read it as seconds. The two
 * are indistinguishable on the wire and differ by a factor of a thousand, so
 * this refuses to guess — `parse("500")` is `null`, and `500ms` or `500s` says
 * which was meant.
 * ------------------------------------------------------------------------- */

/** Seconds in each simple-format unit suffix. Ordered longest-suffix-first. */
const DURATION_UNITS: ReadonlyArray<readonly [string, number]> = [
  ["ns", 1e-9],
  ["us", 1e-6],
  ["ms", 1e-3],
  ["s", 1],
  ["m", 60],
  ["h", 3_600],
  ["d", 86_400],
];

/** `-?<digits>[.<digits>]<unit>` — the simple grammar, anchored. */
const SIMPLE_DURATION_RE = /^([+-]?\d+(?:\.\d+)?)(ns|us|ms|s|m|h|d)$/;

/**
 * `[-]PnDTnHnMn[.n]S` — ISO-8601 minus the calendar designators.
 *
 * Every component is optional, but at least one must be present (a bare `P` or
 * `PT` is rejected by the digit check in `parseDuration`), and a `T` with
 * nothing after it is not a duration either.
 */
const ISO_DURATION_RE =
  /^([+-])?P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;

/**
 * A duration string in SECONDS, or `null` when the input is not one.
 *
 * `null` rather than `0` or `NaN`, because every caller has to tell "not a
 * duration" apart from "a duration of zero": the first is a typo and the second
 * is a legitimate value that merely may be out of range.
 *
 * A number is passed through as seconds — that is the unit this whole namespace
 * speaks, and it makes `parse(parse(x))` a no-op.
 */
export const parseDuration = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value ?? "").trim();
  if (text === "") return null;

  const simple = SIMPLE_DURATION_RE.exec(text);
  if (simple) {
    const unit = DURATION_UNITS.find(([suffix]) => suffix === simple[2]);
    return unit ? Number(simple[1]) * unit[1] : null;
  }

  // Case-insensitive, but only AFTER the simple form has had its turn: `M` is
  // minutes in ISO-8601 while `m` is minutes and `ms` milliseconds in the simple
  // one, so upper-casing first would read `500ms` as an ISO fragment.
  const iso = ISO_DURATION_RE.exec(text);
  // A duration needs at least one number in it; `P`, `PT` and `-PT` match the
  // pattern and carry none.
  if (!iso || !/\d/.test(text)) return null;

  const days = Number(iso[2] ?? 0);
  const hours = Number(iso[3] ?? 0);
  const minutes = Number(iso[4] ?? 0);
  const seconds = Number(iso[5] ?? 0);
  const total = days * 86_400 + hours * 3_600 + minutes * 60 + seconds;
  return iso[1] === "-" ? -total : total;
};

/**
 * Seconds as a simple-format string, in the largest unit that divides evenly —
 * `90` → `"90s"`, `120` → `"2m"`, `5_400` → `"90m"`, `86_400` → `"1d"`.
 *
 * Whole units only, and that is the point: `5400` is exactly 90 minutes and
 * `"1.5h"` would be a lossier way of saying the same thing, while `5401` has no
 * whole-minute form and stays `"5401s"`. Nothing is ever rounded away.
 */
const formatSimpleDuration = (seconds: number): string => {
  if (seconds === 0) return "0s";
  const sign = seconds < 0 ? "-" : "";
  const magnitude = Math.abs(seconds);
  // Largest first, so the shortest exact spelling wins.
  for (let i = DURATION_UNITS.length - 1; i >= 0; i -= 1) {
    const entry = DURATION_UNITS[i];
    if (!entry) continue;
    const [suffix, size] = entry;
    const scaled = magnitude / size;
    if (scaled >= 1 && Number.isInteger(scaled)) return `${sign}${scaled}${suffix}`;
  }
  return `${sign}${magnitude}s`;
};

/**
 * Seconds as an ISO-8601 duration — `P1DT2H30M`, `PT45S`, `PT0S` for zero.
 *
 * Zero renders as `PT0S` rather than `P`, matching `java.time.Duration.ZERO`,
 * because `P` alone is not a duration any parser accepts — including this one.
 */
const formatIsoDuration = (seconds: number): string => {
  if (seconds === 0) return "PT0S";
  const sign = seconds < 0 ? "-" : "";
  let rest = Math.abs(seconds);
  const days = Math.floor(rest / 86_400);
  rest -= days * 86_400;
  const hours = Math.floor(rest / 3_600);
  rest -= hours * 3_600;
  const minutes = Math.floor(rest / 60);
  rest -= minutes * 60;
  // Trailing zeros trimmed: `0.5` stays `0.5`, `2` stays `2`, `2.000` never
  // appears. `Number`'s own string form already does this.
  const secs = Number(rest.toFixed(9));
  const date = days > 0 ? `${days}D` : "";
  const time = [
    hours > 0 ? `${hours}H` : "",
    minutes > 0 ? `${minutes}M` : "",
    secs > 0 ? `${secs}S` : "",
  ].join("");
  return `${sign}P${date}${time ? `T${time}` : ""}`;
};

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAYS_LONG    = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const formatDateTokens = (date: Date, pattern: string): string => {
  const pad = (n: number, w = 2): string => String(n).padStart(w, "0");
  const hours = date.getHours();
  const hour12 = ((hours + 11) % 12) + 1;
  const tokens: Array<[RegExp, string]> = [
    [/YYYY/g, String(date.getFullYear())],
    [/YY/g, String(date.getFullYear()).slice(-2)],
    [/MMMM/g, MONTHS_LONG[date.getMonth()]!],
    [/MMM/g, MONTHS_SHORT[date.getMonth()]!],
    [/MM/g, pad(date.getMonth() + 1)],
    [/dddd/g, DAYS_LONG[date.getDay()]!],
    [/ddd/g, DAYS_SHORT[date.getDay()]!],
    [/DD/g, pad(date.getDate())],
    [/HH/g, pad(hours)],
    [/hh/g, pad(hour12)],
    [/mm/g, pad(date.getMinutes())],
    [/ss/g, pad(date.getSeconds())],
    [/\bM\b/g, String(date.getMonth() + 1)],
    [/\bD\b/g, String(date.getDate())],
    [/\bH\b/g, String(hours)],
    [/\bh\b/g, String(hour12)],
    [/\bm\b/g, String(date.getMinutes())],
    [/\bs\b/g, String(date.getSeconds())],
    [/A/g, hours >= 12 ? "PM" : "AM"],
    [/a/g, hours >= 12 ? "pm" : "am"],
  ];
  let out = pattern;
  for (const [re, value] of tokens) out = out.replace(re, value);
  return out;
};

const formatRelative = (date: Date, now = Date.now()): string => {
  const diff = now - date.getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const minute = 60_000, hour = 60 * minute, day = 24 * hour;
  const week = 7 * day, month = 30 * day, year = 365 * day;
  let value: number, unit: string;
  if (abs < minute) return future ? "in a moment" : "just now";
  if (abs < hour) { value = Math.round(abs / minute); unit = "m"; }
  else if (abs < day) { value = Math.round(abs / hour); unit = "h"; }
  else if (abs < week) { value = Math.round(abs / day); unit = "d"; }
  else if (abs < month) { value = Math.round(abs / week); unit = "w"; }
  else if (abs < year) { value = Math.round(abs / month); unit = "mo"; }
  else { value = Math.round(abs / year); unit = "y"; }
  return future ? `in ${value}${unit}` : `${value}${unit} ago`;
};

interface FormatOptions {
  currency?: string;
  locale?: string;
  decimals?: number;
}

const normalizeFormatOptions = (
  mode: string,
  third: unknown,
  fourth: unknown,
): FormatOptions => {
  if (isObject(third)) {
    const out: FormatOptions = {};
    if (typeof third.currency === "string") out.currency = third.currency;
    if (typeof third.locale === "string") out.locale = third.locale;
    if (typeof third.decimals === "number") out.decimals = third.decimals;
    return out;
  }
  const out: FormatOptions = {};
  if (mode === "currency") {
    if (typeof third === "string" && third.length > 0) out.currency = third;
    if (typeof fourth === "string" && fourth.length > 0) out.locale = fourth;
  } else if (typeof third === "string" && third.length > 0) {
    out.locale = third;
  }
  return out;
};

const recase = (input: unknown, kind: "camel" | "pascal" | "snake" | "kebab"): string => {
  const text = String(input ?? "");
  const parts = text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((p) => p.toLowerCase());
  if (parts.length === 0) return "";
  if (kind === "snake") return parts.join("_");
  if (kind === "kebab") return parts.join("-");
  const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
  if (kind === "pascal") return parts.map(cap).join("");
  return parts[0]! + parts.slice(1).map(cap).join("");
};

/**
 * Pure helpers exposed to programs as the `Util` namespace.
 *
 * All methods are side-effect-free and safe inside reactive expressions.
 * Authors typically reach for one of two styles:
 *
 *   total = Util.sum($cart.price)
 *   text  = Util.plural(items.length, "item")
 *
 * Comparators for `filter` / `find` accept the operator strings
 * `"=="`, `"!="`, `">"`, `"<"`, `">="`, `"<="`, `"contains"`,
 * `"startsWith"`, `"endsWith"`. The string operators (`contains` /
 * `startsWith` / `endsWith`) match case-insensitively.
 */
/**
 * Test a DSL-supplied regex against a DSL-supplied subject with bounded cost.
 *
 * Both the pattern and the subject are untrusted, which is the textbook ReDoS
 * setup: a pattern like `(a+)+$` against a long run of `a` backtracks
 * exponentially and freezes the render thread. An arbitrary pattern cannot be
 * made safe, so the mitigation bounds the subject — worst-case backtracking
 * grows with subject length, so a small cap keeps the worst case small.
 * Over-long subjects are truncated rather than rejected so ordinary matching
 * still behaves as authors expect.
 */
const REGEX_MAX_PATTERN_LENGTH = 1024;
const REGEX_MAX_SUBJECT_LENGTH = 8 * 1024;

export function safeRegexTest(pattern: string, subject: string): boolean {
  if (pattern.length > REGEX_MAX_PATTERN_LENGTH) return false;
  const bounded = subject.length > REGEX_MAX_SUBJECT_LENGTH
    ? subject.slice(0, REGEX_MAX_SUBJECT_LENGTH)
    : subject;
  try { return new RegExp(pattern).test(bounded); }
  catch { return false; }
}

export const Util = {
  // ── Aggregation ───────────────────────────────────────────
  count: <A>(arr: UtilAggregateInput<A>): number => toArray(arr).length,
  sum: <A>(arr: UtilAggregateInput<A>): number =>
    toArray(arr).reduce<number>((a, v) => a + toNumber(v), 0),
  avg: <A>(arr: UtilAggregateInput<A>): number => {
    const xs = toArray(arr);
    return xs.length === 0 ? 0 : xs.reduce<number>((a, v) => a + toNumber(v), 0) / xs.length;
  },
  min: <A>(arr: UtilAggregateInput<A>): number => {
    const xs = toArray(arr).map(toNumber);
    return xs.length === 0 ? 0 : Math.min(...xs);
  },
  max: <A>(arr: UtilAggregateInput<A>): number => {
    const xs = toArray(arr).map(toNumber);
    return xs.length === 0 ? 0 : Math.max(...xs);
  },
  first: <T>(arr: UtilList<T>): T | null => (toArray(arr) as T[])[0] ?? null,
  last: <T>(arr: UtilList<T>): T | null => {
    const xs = toArray(arr) as T[];
    return xs.length === 0 ? null : xs[xs.length - 1] as T;
  },

  // ── Reshaping ─────────────────────────────────────────────
  filter: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = "", op: UtilCompareOp = "==", value?: unknown): T[] =>
    (toArray(arr) as T[]).filter((item) => compare(op, getField(item, String(field)), value)),
  find: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = "", op: UtilCompareOp = "==", value?: unknown): T | null =>
    (toArray(arr) as T[]).find((item) => compare(op, getField(item, String(field)), value)) ?? null,
  sort: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = "", direction: "asc" | "desc" = "asc"): T[] => {
    const xs = [...toArray(arr)] as T[];
    const dir = String(direction).toLowerCase() === "desc" ? -1 : 1;
    xs.sort((a, b) => {
      const av = getField(a, String(field));
      const bv = getField(b, String(field));
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
    return xs;
  },
  groupBy: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = ""): Record<string, T[]> => {
    const out: Record<string, T[]> = {};
    for (const item of toArray(arr) as T[]) {
      const key = String(getField(item, String(field)) ?? "");
      (out[key] ??= []).push(item);
    }
    return out;
  },
  slice: <T>(arr: UtilList<T>, start?: number, end?: number): T[] => {
    const xs = toArray(arr) as T[];
    return xs.slice(start ?? 0, end ?? xs.length);
  },
  unique: <T>(arr: UtilList<T>, field?: UtilFieldPath<T>): T[] => {
    const xs = toArray(arr) as T[];
    if (!field) return Array.from(new Set(xs));
    const seen = new Set<unknown>();
    const out: T[] = [];
    for (const item of xs) {
      const key = getField(item, field);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  },
  reverse: <T>(arr: UtilList<T>): T[] => ([...toArray(arr)] as T[]).reverse(),
  range: (start: number, end: number, step?: number): number[] => {
    const a = toNumber(start), b = toNumber(end);
    const s = step === undefined ? (b >= a ? 1 : -1) : toNumber(step);
    if (s === 0) return [a];
    const span = Math.abs(b - a);
    const count = Math.floor(span / Math.abs(s)) + 1;
    if (count > MAX_RANGE) {
      throw new RangeError(
        `$util.range refusing to allocate ${count} entries (limit ${MAX_RANGE}).`,
      );
    }
    const out: number[] = [];
    if (s > 0) { for (let n = a; n <= b; n += s) out.push(n); }
    else       { for (let n = a; n >= b; n += s) out.push(n); }
    return out;
  },
  repeat: <T>(value: T, n: number): T[] => {
    const count = Math.max(0, toNumber(n));
    if (count > MAX_RANGE) {
      throw new RangeError(
        `$util.repeat refusing to allocate ${count} entries (limit ${MAX_RANGE}).`,
      );
    }
    return Array.from({ length: count }, () => value);
  },
  pick: <T extends object | null | undefined, K extends keyof NonNullable<T> & string>(obj: T, keys: readonly K[]): UtilPicked<T, K> => {
    if (!isObject(obj)) return {} as UtilPicked<T, K>;
    const ks = toArray(keys).map((k) => String(k ?? ""));
    const out: Record<string, unknown> = {};
    for (const k of ks) {
      if (k in obj) out[k] = obj[k];
    }
    return out as UtilPicked<T, K>;
  },
  omit: <T extends object | null | undefined, K extends keyof NonNullable<T> & string>(obj: T, keys: readonly K[]): UtilOmitted<T, K> => {
    if (!isObject(obj)) return {} as UtilOmitted<T, K>;
    const drop = new Set(toArray(keys).map((k) => String(k ?? "")));
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) if (!drop.has(k)) out[k] = obj[k];
    return out as UtilOmitted<T, K>;
  },
  chunk: <T>(arr: UtilList<T>, size: number): T[][] => {
    const xs = toArray(arr) as T[];
    const n = Math.max(1, Math.floor(toNumber(size)));
    const out: T[][] = [];
    for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
    return out;
  },
  flatten: <A extends readonly unknown[], D extends number = 1>(arr: A | null | undefined, depth: D = 1 as D): FlatArray<A, D>[] => {
    const walk = (xs: unknown[], left: number): unknown[] =>
      left <= 0
        ? xs.slice()
        : xs.reduce<unknown[]>((acc, v) => {
            if (Array.isArray(v)) acc.push(...walk(v, left - 1));
            else acc.push(v);
            return acc;
          }, []);
    return walk(toArray(arr), Math.max(0, Math.floor(toNumber(depth)))) as FlatArray<A, D>[];
  },
  zip: <L extends readonly (readonly unknown[])[]>(...arrays: L): { -readonly [K in keyof L]: L[K][number] | null }[] => {
    const lists = arrays.map(toArray);
    const len = lists.reduce((m, l) => Math.max(m, l.length), 0);
    const out: unknown[][] = [];
    for (let i = 0; i < len; i += 1) out.push(lists.map((l) => l[i] ?? null));
    return out as { -readonly [K in keyof L]: L[K][number] | null }[];
  },
  partition: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = "", op: UtilCompareOp = "==", value?: unknown): [pass: T[], fail: T[]] => {
    const pass: T[] = [];
    const fail: T[] = [];
    for (const item of toArray(arr) as T[]) {
      (compare(op, getField(item, String(field)), value) ? pass : fail).push(item);
    }
    return [pass, fail];
  },
  keyBy: <T>(arr: UtilList<T>, field: UtilFieldPath<T> = ""): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const item of toArray(arr) as T[]) {
      out[String(getField(item, String(field)) ?? "")] = item;
    }
    return out;
  },
  cloneDeep: <T>(value: T): T => cloneDeepValue(value),
  merge: (target: unknown, ...sources: unknown[]): Record<string, unknown> => {
    const out: Record<string, unknown> = isObject(target) ? cloneDeepValue(target) : {};
    for (const src of sources) {
      if (!isObject(src)) continue;
      for (const k of Object.keys(src)) {
        const sv = src[k];
        const tv = out[k];
        out[k] = isObject(tv) && isObject(sv)
          ? Util.merge(tv, sv)
          : isObject(sv) || Array.isArray(sv)
            ? cloneDeepValue(sv)
            : sv;
      }
    }
    return out;
  },

  // ── Formatting ────────────────────────────────────────────
  /**
   * Locale-aware number formatter. Modes: `"number"` (default),
   * `"currency"`, `"percent"`, `"compact"`. Options object:
   * `{ currency?, locale?, decimals? }`. Legacy positional form
   * `Util.format(v, "currency", "USD", "en-US")` is also accepted.
   */
  format: (
    value: number | string | null | undefined,
    mode: "number" | "currency" | "percent" | "compact" = "number",
    options?: UtilNumberFormatOptions | string,
    fourth?: string,
  ): string => {
    const v = toNumber(value);
    const m = String(mode);
    const opts = normalizeFormatOptions(m, options, fourth);
    const fmtOptions: Intl.NumberFormatOptions = {};
    if (m === "currency") {
      fmtOptions.style = "currency";
      fmtOptions.currency = opts.currency || "USD";
    } else if (m === "percent") {
      fmtOptions.style = "percent";
      if (opts.decimals === undefined) fmtOptions.maximumFractionDigits = 2;
    } else if (m === "compact") {
      fmtOptions.notation = "compact";
    } else {
      fmtOptions.style = "decimal";
    }
    if (opts.decimals !== undefined) {
      fmtOptions.minimumFractionDigits = opts.decimals;
      fmtOptions.maximumFractionDigits = opts.decimals;
    }
    try {
      return new Intl.NumberFormat(opts.locale || undefined, fmtOptions).format(v);
    } catch {
      return m === "currency" ? v.toFixed(opts.decimals ?? 2) : String(v);
    }
  },
  /**
   * Format a date. Second argument is either a moment-like pattern
   * (`"MMM D"`, `"YYYY-MM-DD"`) or one of: `"relative"`, `"date"`,
   * `"time"`, `"datetime"`, `"iso"`.
   */
  formatDate: (
    value: Date | number | string,
    format: "relative" | "date" | "time" | "datetime" | "iso" | (string & {}) = "MMM D",
  ): string => {
    const date = toDate(value);
    const mode = String(format);
    switch (mode) {
      case "relative": return formatRelative(date);
      case "date": return date.toLocaleDateString();
      case "time": return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      case "datetime": return date.toLocaleString();
      case "iso": return date.toISOString();
      default: return formatDateTokens(date, mode);
    }
  },
  plural: (count: unknown, singular: unknown, plural?: unknown): string => {
    const n = toNumber(count);
    const s = String(singular ?? "");
    const p = plural === undefined ? s + "s" : String(plural ?? "");
    return `${n} ${n === 1 ? s : p}`;
  },
  capitalize: (text: unknown): string => {
    const t = String(text ?? "");
    return t.length === 0 ? "" : t.charAt(0).toUpperCase() + t.slice(1);
  },
  lowercase: (text: unknown): string => String(text ?? "").toLowerCase(),
  uppercase: (text: unknown): string => String(text ?? "").toUpperCase(),
  titlecase: (text: unknown): string => String(text ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" "),
  case: (text: unknown, kind: "camel" | "snake" | "kebab" | "pascal" = "camel"): string => {
    const k = String(kind).toLowerCase();
    return recase(text, (k === "snake" || k === "kebab" || k === "pascal" || k === "camel") ? k : "camel");
  },

  // ── Date / time ───────────────────────────────────────────
  now: (): number => Date.now(),
  today: (): string => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  },
  addDays: (date: Date | number | string, days: number): string => {
    const d = toDate(date);
    const next = new Date(d.getTime());
    next.setDate(next.getDate() + toNumber(days));
    return next.toISOString();
  },
  addHours: (date: Date | number | string, hours: number): string => {
    const d = toDate(date);
    const next = new Date(d.getTime() + toNumber(hours) * 3_600_000);
    return next.toISOString();
  },
  diffDays: (start: Date | number | string, end: Date | number | string): number => {
    const a = toDate(start), b = toDate(end);
    return Math.round((b.getTime() - a.getTime()) / 86_400_000);
  },
  startOfWeek: (date: Date | number | string): string => {
    const d = toDate(date);
    const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    next.setUTCDate(next.getUTCDate() - next.getUTCDay());
    return next.toISOString();
  },
  endOfMonth: (date: Date | number | string): string => {
    const d = toDate(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
  },

  // ── Duration ──────────────────────────────────────────────
  // A LENGTH of time, as opposed to the point-in-time helpers above. Every
  // member speaks SECONDS, so `parse` and `format` compose in both directions
  // and a bound can be written as a plain number.
  //
  // Grouped as a nested namespace rather than as `parseDuration` /
  // `formatDuration` at the top level, so `$util.duration.parse(v)` reads as
  // one idea and the pair cannot drift apart in the catalogue.
  duration: {
    /**
     * A duration string in seconds, or `null` when the value is not one.
     *
     * Accepts the simple format (`250ms`, `30s`, `5m`, `2h`, `7d`) and ISO-8601
     * (`PT30S`, `P1DT12H`), which is what makes one field usable against an API
     * that documents one grammar and an operator who knows the other. Rejects
     * years, months, weeks and unitless numbers — see the note above the
     * parser for each reason.
     */
    parse: (value: unknown): number | null => parseDuration(value),

    /**
     * Seconds as a duration string.
     *
     * `style: "simple"` (the default) picks the largest unit that divides
     * evenly — `120` → `"2m"` — and `style: "iso"` emits `PnDTnHnMnS`. Pass a
     * string and it is re-formatted, so `format("PT120S")` is `"2m"`; pass
     * something that is not a duration at all and you get `""` rather than an
     * exception, in keeping with every other member here.
     */
    format: (value: number | string, options?: { readonly style?: "simple" | "iso" }): string => {
      const seconds = parseDuration(value);
      if (seconds === null) return "";
      const opts = isObject(options) ? options : {};
      return String(opts.style ?? "simple").toLowerCase() === "iso"
        ? formatIsoDuration(seconds)
        : formatSimpleDuration(seconds);
    },

    /** Whether the value parses as a duration at all. */
    isValid: (value: unknown): boolean => parseDuration(value) !== null,
  },

  // ── String / regex helpers ────────────────────────────────
  join: <A>(arr: UtilAggregateInput<A>, sep = ","): string =>
    toArray(arr).map((v) => (v == null ? "" : String(v))).join(String(sep)),
  split: (text: unknown, sep = ","): string[] => String(text ?? "").split(String(sep)),
  trim: (text: unknown): string => String(text ?? "").trim(),
  replace: (text: unknown, search: unknown, replacement: unknown = ""): string =>
    String(text ?? "").split(String(search ?? "")).join(String(replacement ?? "")),
  substring: (text: unknown, start: unknown, end?: unknown): string => {
    const t = String(text ?? "");
    return t.substring(toNumber(start), end === undefined ? t.length : toNumber(end));
  },
  startsWith: (text: unknown, prefix: unknown): boolean =>
    String(text ?? "").startsWith(String(prefix ?? "")),
  endsWith: (text: unknown, suffix: unknown): boolean =>
    String(text ?? "").endsWith(String(suffix ?? "")),
  contains: (text: unknown, needle: unknown): boolean =>
    String(text ?? "").includes(String(needle ?? "")),
  // `pattern` is a regular-expression SOURCE: a RegExp stringifies with its
  // slashes (`String(/b/)` is "/b/"), so passing one never matches as meant.
  match: (text: string | number | null | undefined, pattern: string): boolean => {
    try { return safeRegexTest(String(pattern ?? ""), String(text ?? "")); }
    catch { return false; }
  },

  // ── Math ──────────────────────────────────────────────────
  round: (value: unknown, decimals = 0): number => {
    const factor = Math.pow(10, toNumber(decimals));
    return Math.round(toNumber(value) * factor) / factor;
  },
  floor: (value: unknown): number => Math.floor(toNumber(value)),
  ceil: (value: unknown): number => Math.ceil(toNumber(value)),
  abs: (value: unknown): number => Math.abs(toNumber(value)),
  clamp: (value: unknown, min: unknown, max: unknown): number =>
    Math.min(Math.max(toNumber(value), toNumber(min)), toNumber(max)),
  pow: (base: unknown, exp: unknown): number => Math.pow(toNumber(base), toNumber(exp)),
  sqrt: (value: unknown): number => Math.sqrt(toNumber(value)),
  random: (): number => Math.random(),
  log: (value: unknown): number => Math.log(toNumber(value)),

  // ── Formatting & misc convenience (suggestions-global XIII.6) ─────────
  slugify: (text: unknown): string => String(text ?? "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, ""),
  truncate: (text: unknown, length: unknown = 80, ellipsis = "…"): string => {
    const t = String(text ?? "");
    const n = toNumber(length);
    return t.length <= n ? t : t.slice(0, Math.max(0, n - 1)).trimEnd() + String(ellipsis);
  },
  initials: (name: unknown, max: unknown = 2): string => String(name ?? "")
    .split(/\s+/).filter(Boolean).slice(0, toNumber(max))
    .map((w) => w.charAt(0).toUpperCase()).join(""),
  currency: (value: unknown, code = "USD", locale?: string): string => {
    try { return new Intl.NumberFormat(locale, { style: "currency", currency: String(code) }).format(toNumber(value)); }
    catch { return `${code} ${toNumber(value).toFixed(2)}`; }
  },
  percent: (value: unknown, decimals: unknown = 0): string => {
    const v = toNumber(value);
    return `${(v * 100).toFixed(toNumber(decimals))}%`;
  },
  bytes: (value: unknown): string => {
    let n = toNumber(value);
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
    return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
  },
  relativeTime: (value: Date | number | string): string => {
    const d = toDate(value);
    const diff = d.getTime() - Date.now();
    const abs = Math.abs(diff);
    const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
      ["year", 31536e6], ["month", 2592e6], ["week", 6048e5],
      ["day", 864e5], ["hour", 36e5], ["minute", 6e4], ["second", 1e3],
    ];
    try {
      const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
      for (const [unit, ms] of units) {
        if (abs >= ms || unit === "second") return rtf.format(Math.round(diff / ms), unit);
      }
    } catch { /* fall through */ }
    return d.toLocaleString();
  },
  /**
   * Copy text to the clipboard. Resolves `true` only once the async Clipboard
   * API write actually succeeds (permission can deny it), `false` otherwise.
   * `await $util.copy(x)` in an action; plain truthy checks keep working.
   */
  copy: (text: string | number): Promise<boolean> => {
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { clipboard?: { writeText?: (t: string) => Promise<void> } }) : null;
      const write = nav?.clipboard?.writeText?.(String(text ?? ""));
      if (write && typeof write.then === "function") {
        return write.then(() => true, () => false);
      }
      return Promise.resolve(false);
    } catch { return Promise.resolve(false); }
  },
  /** Await a pause: `await $util.sleep(300)`. Capped at 60s. */
  sleep: (ms: number = 0): Promise<void> => {
    const parsed = toNumber(ms);
    const delay = Number.isFinite(parsed) ? Math.max(0, Math.min(60_000, parsed)) : 0;
    return new Promise((resolve) => setTimeout(resolve, delay));
  },
  uuid: (): string => {
    try {
      const c = (typeof crypto !== "undefined" ? crypto : null) as (Crypto & { randomUUID?: () => string }) | null;
      if (c?.randomUUID) return c.randomUUID();
    } catch { /* fall through */ }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  },
  /** Wrap a function so it only fires `wait` ms after the last call. */
  debounceFn: <A extends unknown[]>(fn: (...args: A) => unknown, wait: number = 250): ((...args: A) => void) => {
    const parsed = toNumber(wait);
    const ms = Number.isFinite(parsed) && parsed > 0 ? parsed : 250;
    let timer: ReturnType<typeof setTimeout> | null = null;
    return (...args: A) => {
      if (typeof fn !== "function") return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { fn(...args); }, ms);
    };
  },
  /**
   * Wrap a function so it fires at most once per `wait` ms. Leading edge
   * fires immediately; calls landing inside the window schedule one trailing
   * fire with the latest arguments, so the final value is never dropped.
   */
  throttleFn: <A extends unknown[]>(fn: (...args: A) => unknown, wait: number = 250): ((...args: A) => void) => {
    const parsed = toNumber(wait);
    const ms = Number.isFinite(parsed) && parsed > 0 ? parsed : 250;
    let last = 0;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    let lastArgs = [] as unknown[] as A;
    return (...args: A) => {
      if (typeof fn !== "function") return;
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn(...args);
        return;
      }
      lastArgs = args;
      if (trailing) return;
      trailing = setTimeout(() => {
        trailing = null;
        last = Date.now();
        fn(...lastArgs);
      }, ms - (now - last));
    };
  },
  // ── Device / sensor APIs (suggestions-global XII.3) ──────────────────────
  /** Trigger device haptics. `pattern` is ms or an array of on/off ms. */
  vibrate: (pattern: number | readonly number[] = 10): boolean => {
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }) : null;
      if (!nav?.vibrate) return false;
      const p = Array.isArray(pattern) ? (pattern as number[]).map((n) => Number(n) || 0) : (Number(pattern) || 0);
      return nav.vibrate(p);
    } catch { return false; }
  },
  /** Native share sheet. `data` = { title?, text?, url? }. Returns a promise. */
  share: (
    data: string | { readonly title?: string; readonly text?: string; readonly url?: string; readonly files?: readonly UtilBlobLike[] },
  ): Promise<boolean> => {
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { share?: (d: unknown) => Promise<void> }) : null;
      if (!nav?.share) return Promise.resolve(false);
      const d = (data && typeof data === "object") ? data as Record<string, unknown> : { text: String(data ?? "") };
      return nav.share(d).then(() => true).catch(() => false);
    } catch { return Promise.resolve(false); }
  },
  /** Read text from the clipboard (async). Returns "" when unavailable/denied. */
  readClipboard: (): Promise<string> => {
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { clipboard?: { readText?: () => Promise<string> } }) : null;
      if (!nav?.clipboard?.readText) return Promise.resolve("");
      return nav.clipboard.readText().catch(() => "");
    } catch { return Promise.resolve(""); }
  },
  /**
   * Read a file the user picked, and resolve with its contents.
   *
   * `FileUpload` is the only way a program receives a file, and its own note
   * says why the file cannot travel through a `$variable`: a `File` is not
   * serialisable, so it reaches the program as a callback argument and nowhere
   * else. Until now there was no vetted way to then READ it — the only route was
   * reaching for `FileReader` or `document` as a host global, which the `"safe"`
   * global-access policy exists to forbid (`SAFE_HOST_GLOBALS` grants the inert
   * `Blob`/`File` containers but no reader). So a program that wanted the
   * contents of a picked `.pub`, `.csv` or `.json` had to be run under the
   * unrestricted `"all"` policy. This is that read, as a capability the runtime
   * grants rather than one the program smuggles in.
   *
   *   FileUpload("key-file", {accept: ".pub,text/plain", action: onPick})
   *   function onPick(files) {
   *     $util.readFile(files).then(text => { $keyBody = text.trim() })
   *   }
   *
   * `file` may be a single `File`/`Blob`, or the whole pick as `FileUpload`
   * hands it over (a `File[]`; a `FileList` works too) — in which case the
   * FIRST readable entry is used. Loop the pick yourself for `multiple`.
   *
   * `options.as` selects the representation:
   *   `"text"`     (default) the decoded UTF-8 text
   *   `"dataUrl"`  a `data:<mime>;base64,…` URI, for an inline preview
   *   `"base64"`   that URI's payload alone, for a JSON body
   *
   * `options.maxSize` rejects a larger file without reading it, in bytes.
   * `FileUpload`'s own `maxSize` already screens the pick, so this is for the
   * programmatic caller that did not come through the component.
   *
   * NEVER REJECTS. It resolves `""` for every failure — no file, an unreadable
   * one, an over-size one, a host with no reader at all — because `await` in
   * Aktion does not suspend, so an author writes `.then(...)` and a rejection
   * would surface as an unhandled promise instead of at the call site. An empty
   * string is also the honest answer: the program has no contents to work with.
   * Branch on the result being empty, not on a `.catch`.
   */
  readFile: (
    file: UtilBlobLike | ArrayLike<UtilBlobLike> | null | undefined,
    options?: UtilReadFileOptions,
  ): Promise<string> => {
    try {
      const blob = firstBlob(file);
      if (!blob) return Promise.resolve("");
      const opts = isObject(options) ? options : {};
      const limit = Math.max(0, toNumber(opts.maxSize));
      if (limit > 0 && typeof blob.size === "number" && blob.size > limit) return Promise.resolve("");
      const as = typeof opts.as === "string" ? opts.as : "text";
      if (as === "dataUrl" || as === "base64") {
        return blobToDataUrl(blob).then(
          (url) => (as === "base64" ? url.slice(url.indexOf(",") + 1) : url),
          () => "",
        );
      }
      return blobToText(blob).then((text) => text, () => "");
    } catch { return Promise.resolve(""); }
  },
  /**
   * Open a URL in a new browsing context.
   *
   * `Link(label, {href, external: true})` covers the case where the destination
   * is known at render time. This is the other one: a URL an ACTION produced —
   * a documentation deep link built from the row that was clicked, a signed
   * download, an invoice, a support ticket. Until now the only route was
   * reaching for `window` as a host global, which the `"safe"` global-access
   * policy exists to forbid.
   *
   *   MenuItem("Open the manual", {onClick: () => $util.openUrl(row.docsUrl)})
   *
   * `options.target` names the context. The default `"_blank"` opens a new tab;
   * a NAME ("report-window") reuses the same window across clicks, which is the
   * behaviour you want for a console or a preview the user keeps open.
   *
   * `options.features` is a `{width, height, left, top, resizable, scrollbars,
   * menubar, toolbar, location, status}` bag (a `window.open` feature string is
   * accepted and re-parsed). Any key outside that list is dropped. Note that
   * asking for a size is what makes most browsers open a WINDOW rather than a
   * tab.
   *
   * `options.noopener` defaults to `true` for `_blank` and to `false` for a
   * named target — `noopener` makes the browser ignore the name and open a fresh
   * context every time, which would silently defeat the reuse the name was for.
   *
   * ONLY `http:`, `https:`, `mailto:` and `tel:` are opened. `javascript:`,
   * `data:`, `blob:` and `file:` are rejected and answer `false`. Relative URLs
   * are resolved against the document.
   *
   * THE RETURN VALUE IS "WAS THIS A REQUEST WE COULD MAKE", NOT "DID A WINDOW
   * APPEAR". Under `noopener` the browser returns `null` on SUCCESS as well as
   * on a blocked popup, so the two are indistinguishable from here — `false`
   * therefore means only that the URL was rejected or the host has no `window`.
   * Use {@link openWindow} when the program has to react to a popup blocker.
   *
   * A POPUP BLOCKER WILL STOP THIS unless the call happens inside the user
   * gesture that triggered it. Calling it from a `$http` callback — "fetch the
   * signed URL, then open it" — is exactly the shape browsers block. That is
   * what {@link openWindow} is for.
   */
  openUrl: (url: string, options?: UtilOpenUrlOptions): boolean => {
    try {
      const href = openableUrl(url);
      if (!href) return false;
      if (typeof window === "undefined" || typeof window.open !== "function") return false;
      const opts = isObject(options) ? options : {};
      const target = typeof opts.target === "string" && opts.target.trim() ? opts.target.trim() : "_blank";
      const named = !RESERVED_WINDOW_TARGETS.has(target);
      // `undefined` means "use the default for this target"; anything else is read
      // through `isFeatureOn`, the SAME coercion the `features` bag uses — see its
      // note for the inversion that cost.
      const features = windowFeatures(opts.features, {
        noopener: opts.noopener === undefined ? !named : isFeatureOn(opts.noopener),
        noreferrer: isFeatureOn(opts.noreferrer),
      });
      window.open(href, target, features || undefined);
      return true;
    } catch {
      return false;
    }
  },
  /**
   * Open an EMPTY browsing context now, and navigate it once you know where to.
   *
   * The problem this exists for is specific and otherwise unsolvable in the
   * language: a URL that has to be FETCHED before it can be opened — an SSO
   * hand-off, a signed download, an OAuth start URL. Written the obvious way,
   *
   *   const req = $http({url: `/things/${id}/ssourl`})
   *   req.onDone = () => { $util.openUrl(req.data.ssoUrl) }   // ✗ popup-blocked
   *
   * the open happens in a network callback, long after the click that caused it,
   * and every browser's popup blocker stops it. The fix is the same everywhere:
   * open a blank context DURING the click, and point it somewhere when the
   * answer arrives.
   *
   *   function openConsole(row) {
   *     const win = $util.openWindow({name: "console", features: {width: 1024, height: 700}})
   *     const req = $http({url: `/things/${row.id}/ssourl`})
   *     req.onDone = () => {
   *       if (req.error) { win.close(); $toast.error(t("sso_failed")); return }
   *       if (!win.navigate(req.data.ssoUrl)) { $ssoFallbackUrl = req.data.ssoUrl }
   *     }
   *   }
   *
   * `ok` is `false` when the browser refused, which is the case worth handling:
   * a blocked popup is invisible otherwise, and the honest response is to put
   * the URL on screen as a link the operator can click themselves.
   *
   * `options.name` reuses one window across clicks, exactly as `window.open`'s
   * name does. `options.features` is {@link openUrl}'s bag, same allow-list.
   *
   * `noopener` CANNOT be used here — it makes `window.open` return `null`, and
   * the handle is the entire point. Instead the child's `opener` is cleared
   * while it is still the same-origin `about:blank` this opened, so the page it
   * is later navigated to cannot reach back into this one. That is the same
   * protection `noopener` gives, applied in the one order that keeps the handle.
   */
  openWindow: (options?: UtilOpenWindowOptions): OpenedWindow => {
    try {
      if (typeof window === "undefined" || typeof window.open !== "function") return CLOSED_WINDOW;
      const opts = isObject(options) ? options : {};
      const name = typeof opts.name === "string" && opts.name.trim() ? opts.name.trim() : "_blank";
      const features = windowFeatures(opts.features);
      const child = window.open("about:blank", name, features || undefined);
      if (!child) return CLOSED_WINDOW;
      try {
        // Same-origin `about:blank` at this instant, so this write lands; once
        // the context navigates cross-origin the null sticks.
        (child as Window & { opener: unknown }).opener = null;
      } catch {
        // A host that refuses the write (a strict jsdom, an exotic embedder)
        // still gets a usable handle — the mitigation is best-effort, and
        // failing the open over it would be worse than the risk it covers.
      }
      return {
        ok: true,
        navigate(url: string): boolean {
          const href = openableUrl(url);
          if (!href) return false;
          try {
            if (child.closed) return false;
            child.location.href = href;
            return true;
          } catch {
            return false;
          }
        },
        close(): void {
          try {
            if (!child.closed) child.close();
          } catch {
            // Already gone, or a host that forbids programmatic close.
          }
        },
        get closed(): boolean {
          try {
            return child.closed;
          } catch {
            return true;
          }
        },
      };
    } catch {
      return CLOSED_WINDOW;
    }
  },
  /** Current geolocation as a promise of { lat, lng, accuracy } (or null). */
  geolocate: (
    options?: { readonly enableHighAccuracy?: boolean; readonly timeout?: number; readonly maximumAge?: number },
  ): Promise<{ readonly lat: number; readonly lng: number; readonly accuracy: number } | null> => {
    return new Promise((resolve) => {
      try {
        const nav = typeof navigator !== "undefined" ? (navigator as Navigator) : null;
        if (!nav?.geolocation) { resolve(null); return; }
        nav.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          () => resolve(null),
          (options && typeof options === "object") ? options as PositionOptions : undefined,
        );
      } catch { resolve(null); }
    });
  },
  /** `true` when the device is currently online. */
  isOnline: (): boolean => {
    try { return typeof navigator !== "undefined" ? navigator.onLine !== false : true; }
    catch { return true; }
  },
  /** Best-effort device class from the user agent: "mobile" | "tablet" | "desktop". */
  deviceType: (): "mobile" | "tablet" | "desktop" => {
    try {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      if (/iPad|Tablet|PlayBook|Silk/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return "tablet";
      if (/Mobi|Android|iPhone|iPod|Windows Phone/.test(ua)) return "mobile";
      return "desktop";
    } catch { return "desktop"; }
  },
  // ── Web Worker offload (suggestions-global XI.5) ─────────────────────────
  /**
   * Run a PURE function off the main thread in a Web Worker, resolving with its
   * result. `fn` is serialised via `toString()`, so it must not close over
   * outer variables (pass everything it needs as arguments). Falls back to
   * running inline (still async) when Workers aren't available.
   *   $util.worker((n) => heavyCompute(n), 1000).then(r => $result = r)
   *
   * `fn` must be a HOST JavaScript function. An Aktion lambda is the
   * evaluator's closure, so its `toString()` is the runtime's own wrapper (it
   * references the evaluation context), not the program's code — only the
   * no-Worker fallback can run it.
   */
  worker: <A extends unknown[], R>(fn: (...args: A) => R, ...args: A): Promise<Awaited<R>> => {
    if (typeof fn !== "function") return Promise.resolve(undefined) as Promise<Awaited<R>>;
    const fallback = (): Promise<Awaited<R>> =>
      Promise.resolve().then(() => fn(...args)) as Promise<Awaited<R>>;
    const canWorker =
      typeof Worker !== "undefined" && typeof Blob !== "undefined" &&
      typeof URL !== "undefined" && typeof URL.createObjectURL === "function";
    if (!canWorker) return fallback();
    try {
      const src =
        `self.onmessage=function(e){` +
        `var fn=(${(fn as () => unknown).toString()});` +
        `Promise.resolve().then(function(){return fn.apply(null,e.data);})` +
        `.then(function(r){self.postMessage({ok:true,value:r});})` +
        `.catch(function(err){self.postMessage({ok:false,error:String(err)});});};`;
      const url = URL.createObjectURL(new Blob([src], { type: "application/javascript" }));
      const w = new Worker(url);
      return new Promise((resolve, reject) => {
        w.onmessage = (e: MessageEvent) => {
          URL.revokeObjectURL(url); w.terminate();
          const data = e.data as { ok?: boolean; value?: unknown; error?: string };
          if (data?.ok) resolve(data.value as Awaited<R>);
          else reject(new Error(data?.error || "worker error"));
        };
        w.onerror = (err) => { URL.revokeObjectURL(url); w.terminate(); reject(err); };
        w.postMessage(args);
      });
    } catch {
      return fallback();
    }
  },
  // ── PWA helpers (suggestions-global XII.2) ───────────────────────────────
  /** Register a service worker. Resolves true on success, false otherwise. */
  registerServiceWorker: (url: string, scope?: string): Promise<boolean> => {
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator) : null;
      if (!nav?.serviceWorker || typeof url !== "string" || !url) return Promise.resolve(false);
      const opts = typeof scope === "string" && scope ? { scope } : undefined;
      return nav.serviceWorker.register(url, opts).then(() => true).catch(() => false);
    } catch { return Promise.resolve(false); }
  },
  /**
   * Build a sanitised Web App Manifest object from a config (XII.2). Use it to
   * inline a manifest (`<link rel="manifest" href="data:...">`) or write one at
   * build time. Unknown/unsafe keys are dropped.
   */
  webManifest: (config: UtilWebManifestConfig): UtilWebManifest => {
    const cfg = (config && typeof config === "object" && !Array.isArray(config)) ? config as Record<string, unknown> : {};
    const out: UtilWebManifest = {
      name: typeof cfg.name === "string" ? cfg.name : "App",
      short_name: typeof cfg.shortName === "string" ? cfg.shortName : (typeof cfg.name === "string" ? cfg.name : "App"),
      start_url: typeof cfg.startUrl === "string" ? cfg.startUrl : "/",
      display: typeof cfg.display === "string" ? cfg.display : "standalone",
      background_color: typeof cfg.backgroundColor === "string" ? cfg.backgroundColor : "#ffffff",
      theme_color: typeof cfg.themeColor === "string" ? cfg.themeColor : "#000000",
    };
    if (typeof cfg.description === "string") out.description = cfg.description;
    if (Array.isArray(cfg.icons)) {
      out.icons = cfg.icons
        .filter((i): i is Record<string, unknown> => Boolean(i) && typeof i === "object")
        .map((i) => ({ src: String(i.src ?? ""), sizes: String(i.sizes ?? "512x512"), type: String(i.type ?? "image/png") }));
    }
    return out;
  },
  // ── Native shell detection (suggestions-global XII.4) ────────────────────
  /**
   * Detect the native shell the app is running inside (Capacitor / Cordova /
   * Tauri / Electron / React Native WebView), or "web" when it's a plain
   * browser. Lets a program branch on the host (e.g. hide a download button in
   * a native shell, or call a bridge when present).
   */
  nativeShell: (): "tauri" | "capacitor" | "cordova" | "react-native" | "electron" | "web" => {
    try {
      const w = typeof window !== "undefined" ? (window as unknown as Record<string, unknown>) : {};
      const nav = typeof navigator !== "undefined" ? navigator : ({ userAgent: "" } as Navigator);
      if (w.__TAURI__ || w.__TAURI_IPC__) return "tauri";
      if (w.Capacitor) return "capacitor";
      if (w.cordova || w.PhoneGap) return "cordova";
      if (w.ReactNativeWebView) return "react-native";
      if (/Electron\//.test(nav.userAgent)) return "electron";
      return "web";
    } catch { return "web"; }
  },
  /** True when running inside any native shell (not a plain browser). */
  isNativeApp: (): boolean => Util.nativeShell() !== "web",
} as const;

// Soft cap on `Util.range` / `Util.repeat` allocations so a stray
// `Util.range(0, 1e9)` cannot wedge the renderer before evaluation
// budgets kick in elsewhere.
const MAX_RANGE = 100_000;

export type UtilNamespace = typeof Util;
